import type { LucideIcon } from "lucide-react/dist/lucide-react.d.ts";
import type { ReactNode } from "react";

export type SectionTone =
  | "neutral"
  | "indigo"
  | "violet"
  | "amber"
  | "cyan"
  | "rose"
  | "emerald";

interface ToneStyles {
  /** Background of the icon disk. */
  iconBg: string;
  /** Foreground (icon stroke) inside the disk. */
  iconFg: string;
  /** Soft tint for the content panel body. */
  panelBg: string;
  /** Left border accent on the content panel. */
  panelBorder: string;
  /** Subtle ring around the icon disk. */
  iconRing: string;
}

const TONES: Record<SectionTone, ToneStyles> = {
  neutral: {
    iconBg: "bg-zinc-100 dark:bg-zinc-800",
    iconFg: "text-zinc-700 dark:text-zinc-200",
    panelBg: "bg-zinc-50/70 dark:bg-zinc-900/50",
    panelBorder: "border-zinc-300/70 dark:border-zinc-700/70",
    iconRing: "ring-zinc-200 dark:ring-zinc-700",
  },
  indigo: {
    iconBg: "bg-indigo-100 dark:bg-indigo-950/60",
    iconFg: "text-indigo-700 dark:text-indigo-300",
    panelBg: "bg-indigo-50/60 dark:bg-indigo-950/30",
    panelBorder: "border-indigo-300/60 dark:border-indigo-800/60",
    iconRing: "ring-indigo-200 dark:ring-indigo-800",
  },
  violet: {
    iconBg: "bg-violet-100 dark:bg-violet-950/60",
    iconFg: "text-violet-700 dark:text-violet-300",
    panelBg: "bg-violet-50/60 dark:bg-violet-950/30",
    panelBorder: "border-violet-300/60 dark:border-violet-800/60",
    iconRing: "ring-violet-200 dark:ring-violet-800",
  },
  amber: {
    iconBg: "bg-amber-100 dark:bg-amber-950/60",
    iconFg: "text-amber-700 dark:text-amber-300",
    panelBg: "bg-amber-50/60 dark:bg-amber-950/30",
    panelBorder: "border-amber-300/60 dark:border-amber-800/60",
    iconRing: "ring-amber-200 dark:ring-amber-800",
  },
  cyan: {
    iconBg: "bg-cyan-100 dark:bg-cyan-950/60",
    iconFg: "text-cyan-700 dark:text-cyan-300",
    panelBg: "bg-cyan-50/60 dark:bg-cyan-950/30",
    panelBorder: "border-cyan-300/60 dark:border-cyan-800/60",
    iconRing: "ring-cyan-200 dark:ring-cyan-800",
  },
  rose: {
    iconBg: "bg-rose-100 dark:bg-rose-950/60",
    iconFg: "text-rose-700 dark:text-rose-300",
    panelBg: "bg-rose-50/60 dark:bg-rose-950/30",
    panelBorder: "border-rose-300/60 dark:border-rose-800/60",
    iconRing: "ring-rose-200 dark:ring-rose-800",
  },
  emerald: {
    iconBg: "bg-emerald-100 dark:bg-emerald-950/60",
    iconFg: "text-emerald-700 dark:text-emerald-300",
    panelBg: "bg-emerald-50/60 dark:bg-emerald-950/30",
    panelBorder: "border-emerald-300/60 dark:border-emerald-800/60",
    iconRing: "ring-emerald-200 dark:ring-emerald-800",
  },
};

interface NodeSectionProps {
  label: string;
  icon: LucideIcon;
  tone?: SectionTone;
  children: ReactNode;
}

/**
 * Carte de section "carte mentale" : un disque d'icône coloré relié au tronc
 * vertical de la carte, suivi d'un panneau teinté contenant le contenu.
 */
export function NodeSection({
  label,
  icon: Icon,
  tone = "neutral",
  children,
}: NodeSectionProps) {
  const t = TONES[tone];
  return (
    <section className="relative flex gap-2.5">
      {/* Disque d'icône — accroché au tronc */}
      <div className="relative shrink-0">
        <div
          className={[
            "relative z-10",
            "flex h-8 w-8 items-center justify-center",
            "rounded-full ring-2",
            t.iconBg,
            t.iconRing,
            "shadow-sm",
          ].join(" ")}
        >
          <Icon className={`h-5.5 w-5.5 ${t.iconFg}`} aria-hidden="true" />
        </div>
      </div>

      {/* Panneau de contenu */}
      <div className="flex-1 min-w-0">
        <h4
          className={`mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] ${t.iconFg}`}
        >
          {label}
        </h4>
        <div
          className={[
            "rounded-lg border-l-2",
            t.panelBg,
            t.panelBorder,
            "px-2.5 py-2",
            "text-[11px] leading-snug text-zinc-900 dark:text-zinc-50",
          ].join(" ")}
        >
          {children}
        </div>
      </div>
    </section>
  );
}
